import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Users, Plus, Trash2, Edit2, Download, Link2, Check, Copy, UserPlus, X, DollarSign, Clock, CheckCheck, RotateCcw, Ban, CreditCard, AlertTriangle } from 'lucide-react';

const AdminReferrals = () => {
  const [helpers, setHelpers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutSummary, setPayoutSummary] = useState({ pendingTotal: 0, eligibleTotal: 0, approvedTotal: 0, paidTotal: 0, voidTotal: 0, cooldownDays: 30, minPayoutEur: 20 });
  const [payoutFilter, setPayoutFilter] = useState('pending');
  const [payoutTypeFilter, setPayoutTypeFilter] = useState('');  // '' | 'initial_commission' | 'renewal_commission'
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', referralCode: '', notes: '' });
  const [assignForm, setAssignForm] = useState({ businessId: '', helperId: '' });
  const [message, setMessage] = useState(null);
  const [selectedHelper, setSelectedHelper] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [section, setSection] = useState('helpers'); // 'helpers' | 'payouts'
  // Iter 177 — Sort key for the helpers table. Default: total earned desc.
  const [helperSortBy, setHelperSortBy] = useState('totalEarned');
  const [payPrompt, setPayPrompt] = useState(null); // { id, ref }
  // Commission override modal — Feb 2026 admin hardening
  const [commissionModal, setCommissionModal] = useState(null); // referral object
  const [commissionForm, setCommissionForm] = useState({ amount: '', reason: '' });
  const [announceState, setAnnounceState] = useState({ counts: null, busy: false, error: null });

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (section === 'payouts') fetchPayouts(); }, [section, payoutFilter, payoutTypeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [hRes, rRes] = await Promise.all([
        axiosInstance.get('/admin/helpers'),
        axiosInstance.get('/admin/referrals'),
      ]);
      setHelpers(hRes.data.helpers || []);
      setReferrals(rRes.data.referrals || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPayouts = async () => {
    try {
      const qs = new URLSearchParams();
      if (payoutFilter) qs.set('status', payoutFilter);
      if (payoutTypeFilter) qs.set('type', payoutTypeFilter);
      const params = qs.toString() ? `?${qs}` : '';
      const res = await axiosInstance.get(`/admin/payouts${params}`);
      setPayouts(res.data.referrals || []);
      setPayoutSummary(res.data.summary || { pendingTotal: 0, eligibleTotal: 0, approvedTotal: 0, paidTotal: 0, voidTotal: 0, initialTotal: 0, renewalTotal: 0, renewalCount: 0, cooldownDays: 30, minPayoutEur: 20 });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load payouts' });
    }
  };

  const approvePayout = async (id) => {
    try {
      await axiosInstance.put(`/admin/payouts/${id}/approve`);
      flash('success', 'Payout approved');
      fetchPayouts();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to approve');
    }
  };

  const markPayoutPaid = async (id, paymentReference) => {
    try {
      await axiosInstance.put(`/admin/payouts/${id}/mark-paid`, { paymentReference });
      flash('success', 'Marked as paid');
      setPayPrompt(null);
      fetchPayouts();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed');
    }
  };

  const revokePayout = async (id, makeVoid) => {
    const reason = window.prompt(makeVoid ? 'Reason for voiding this payout?' : 'Reason for sending back to pending?') || '';
    try {
      await axiosInstance.put(`/admin/payouts/${id}/revoke`, { void: makeVoid, reason });
      flash('success', makeVoid ? 'Payout voided' : 'Payout reset to pending');
      fetchPayouts();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed');
    }
  };

  const approveAllEligible = async () => {
    if (!window.confirm('Bulk-approve every eligible payout (past cooldown, not refunded)?')) return;
    try {
      const res = await axiosInstance.post('/admin/payouts/approve-all-eligible');
      flash('success', res.data.message);
      fetchPayouts();
    } catch (err) {
      flash('error', 'Failed to bulk approve');
    }
  };

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 4000); };

  const submitCommissionOverride = async () => {
    if (!commissionModal || !commissionForm.reason.trim() || commissionForm.amount === '') return;
    const amount = parseFloat(commissionForm.amount);
    if (isNaN(amount) || amount < 0) { flash('error', 'Amount must be a non-negative number'); return; }
    try {
      const res = await axiosInstance.put(`/admin/referrals/${commissionModal.id}/commission`, {
        amount, reason: commissionForm.reason.trim(),
      });
      flash('success', `Commission changed from €${res.data.previousAmount.toFixed(2)} to €${res.data.newAmount.toFixed(2)}`);
      setCommissionModal(null); setCommissionForm({ amount: '', reason: '' });
      fetchPayouts();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to update commission');
    }
  };

  const createHelper = async () => {
    if (!form.name.trim()) return flash('error', 'Name is required');
    try {
      const res = await axiosInstance.post('/admin/helpers', form);
      flash('success', res.data.message);
      setShowAdd(false);
      setForm({ name: '', email: '', referralCode: '', notes: '' });
      fetchData();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to create');
    }
  };

  const updateHelper = async (id) => {
    try {
      await axiosInstance.put(`/admin/helpers/${id}`, form);
      flash('success', 'Helper updated');
      setEditingId(null);
      fetchData();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteHelper = async (id, name) => {
    if (!window.confirm(`Delete helper "${name}"? Referral records will remain.`)) return;
    try {
      await axiosInstance.delete(`/admin/helpers/${id}`);
      flash('success', 'Helper deleted');
      fetchData();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to delete');
    }
  };

  const assignReferral = async () => {
    if (!assignForm.businessId || !assignForm.helperId) return flash('error', 'Both fields required');
    try {
      const res = await axiosInstance.post('/admin/referrals/assign', assignForm);
      flash('success', res.data.message);
      setShowAssign(false);
      setAssignForm({ businessId: '', helperId: '' });
      fetchData();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to assign');
    }
  };

  const exportCSV = () => {
    window.open(`${axiosInstance.defaults.baseURL}/admin/referrals/export`, '_blank');
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`https://www.find-2-day.com/business/signup?ref=${code}`).catch(() => {});
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const helperReferrals = (helperId) => referrals.filter(r => r.helperId === helperId);

  // Iter 176 — Per-helper stats derived from existing API data. Splits
  // founder leads from paid signups and counts businesses that converted
  // from a founder lead into a paid referral. Pure read; no API change.
  const helperStats = (helperId) => {
    const rows = referrals.filter(r => r.helperId === helperId);
    const founderRows = rows.filter(r => r.type === 'founder_lead');
    const paidRows = rows.filter(r => r.paymentConfirmed && !r.refunded && r.type !== 'founder_lead');
    const founderBizIds = new Set(founderRows.map(r => r.businessId).filter(Boolean));
    const convertedBizIds = new Set(
      paidRows.map(r => r.businessId).filter(id => founderBizIds.has(id))
    );
    return {
      founderLeads: founderRows.length,
      paidSignups: paidRows.length,
      convertedBusinesses: convertedBizIds.size,
    };
  };
  const totalCommission = helpers.reduce((sum, h) => sum + (h.totalCommission || 0), 0);

  const renderPayoutDetails = (pd, { compact = false } = {}) => {
    if (!pd || (!pd.method && !pd.iban && !pd.paypalEmail && !pd.accountHolder)) {
      return (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5" data-testid="payout-details-missing">
          <AlertTriangle className="h-3.5 w-3.5" />
          Helper has not added payout details yet — cannot pay until they do.
        </div>
      );
    }
    const method = (pd.method || 'sepa').toUpperCase();
    const copy = (val) => { if (!val) return; navigator.clipboard.writeText(val).catch(() => {}); flash('success', 'Copied'); };
    const Row = ({ label, value, mono }) => value ? (
      <div className="flex items-start gap-2 text-xs">
        <span className="text-gray-500 min-w-[90px]">{label}</span>
        <span className={`text-gray-900 break-all flex-1 ${mono ? 'font-mono' : ''}`}>{value}</span>
        <button onClick={() => copy(value)} className="text-gray-400 hover:text-purple-600" title="Copy"><Copy className="h-3 w-3" /></button>
      </div>
    ) : null;
    return (
      <div className={`bg-purple-50 border border-purple-200 rounded-lg ${compact ? 'p-3' : 'p-4'} space-y-1.5`} data-testid="payout-details-panel">
        <div className="flex items-center gap-1.5 mb-1">
          <CreditCard className="h-3.5 w-3.5 text-purple-700" />
          <span className="text-[11px] font-bold text-purple-700 uppercase tracking-wide">Payout: {method}</span>
          {pd.country && <span className="text-[11px] text-gray-500">· {pd.country}</span>}
        </div>
        <Row label="Account holder" value={pd.accountHolder} />
        <Row label="IBAN" value={pd.iban} mono />
        <Row label="BIC / SWIFT" value={pd.bic} mono />
        <Row label="Bank" value={pd.bankName} />
        <Row label="PayPal" value={pd.paypalEmail} />
        <Row label="Notes" value={pd.notes} />
        {pd.updatedAt && <p className="text-[10px] text-gray-400 pt-1">Saved {new Date(pd.updatedAt).toLocaleString()}</p>}
      </div>
    );
  };

  const openPayPrompt = (r) => {
    const pd = r.helperPayoutDetails || {};
    const method = (pd.method || 'sepa').toUpperCase();
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const helperFirst = (r.helperName || '').split(' ')[0] || 'Helper';
    const suggested = `${method}-${today}-${helperFirst}`;
    setPayPrompt({ id: r.id, ref: suggested, payoutDetails: pd, helperName: r.helperName, amount: r.commissionAmount });
  };

  return (
    <div className="space-y-6" data-testid="referrals-tab">
      {/* Section switch: Helpers vs Payouts */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setSection('helpers')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${section === 'helpers' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="section-helpers"
        >
          <Users className="h-4 w-4 inline mr-1.5" /> Helpers & Referrals
        </button>
        <button
          onClick={() => setSection('payouts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${section === 'payouts' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="section-payouts"
        >
          <DollarSign className="h-4 w-4 inline mr-1.5" /> Payouts
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {section === 'payouts' && (
        <div className="space-y-4" data-testid="payouts-section">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
              <p className="text-xs text-amber-700 font-medium uppercase">Pending</p>
              <p className="text-xl font-bold text-amber-900 mt-1" data-testid="payouts-pending-total">{payoutSummary.pendingTotal.toFixed(2)} EUR</p>
              <p className="text-[10px] text-amber-600 mt-1">After {payoutSummary.cooldownDays}d cooldown</p>
            </div>
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
              <p className="text-xs text-blue-700 font-medium uppercase">Eligible Now</p>
              <p className="text-xl font-bold text-blue-900 mt-1" data-testid="payouts-eligible-total">{payoutSummary.eligibleTotal.toFixed(2)} EUR</p>
            </div>
            <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
              <p className="text-xs text-purple-700 font-medium uppercase">Approved</p>
              <p className="text-xl font-bold text-purple-900 mt-1" data-testid="payouts-approved-total">{payoutSummary.approvedTotal.toFixed(2)} EUR</p>
            </div>
            <div className="bg-green-50 rounded-xl border border-green-200 p-4">
              <p className="text-xs text-green-700 font-medium uppercase">Paid</p>
              <p className="text-xl font-bold text-green-900 mt-1" data-testid="payouts-paid-total">{payoutSummary.paidTotal.toFixed(2)} EUR</p>
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-600 font-medium uppercase">Void</p>
              <p className="text-xl font-bold text-gray-700 mt-1">{payoutSummary.voidTotal.toFixed(2)} EUR</p>
            </div>
          </div>

          {/* Type-split breakdown (initial vs renewal) */}
          <div className="grid grid-cols-2 gap-3" data-testid="payouts-type-breakdown">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-600 font-medium uppercase">Initial signups</p>
              <p className="text-xl font-bold text-gray-900 mt-1" data-testid="payouts-initial-total">{(payoutSummary.initialTotal || 0).toFixed(2)} EUR</p>
              <p className="text-[10px] text-gray-500 mt-1">Lifetime, all statuses except void/refunded</p>
            </div>
            <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4">
              <p className="text-xs text-indigo-700 font-medium uppercase">Renewal commissions</p>
              <p className="text-xl font-bold text-indigo-900 mt-1" data-testid="payouts-renewal-total">{(payoutSummary.renewalTotal || 0).toFixed(2)} EUR</p>
              <p className="text-[10px] text-indigo-700 mt-1">{payoutSummary.renewalCount || 0} renewal payment{(payoutSummary.renewalCount || 0) === 1 ? '' : 's'} · 12-month plan only</p>
            </div>
          </div>

          {/* Filter + actions */}
          <div className="flex flex-wrap gap-2 items-center">
            <select value={payoutFilter} onChange={e => setPayoutFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="payout-filter">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="void">Void</option>
            </select>
            <select value={payoutTypeFilter} onChange={e => setPayoutTypeFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="payout-type-filter">
              <option value="">All types</option>
              <option value="initial_commission">Initial only</option>
              <option value="renewal_commission">Renewals only</option>
            </select>
            <button onClick={approveAllEligible} className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700" data-testid="approve-all-btn">
              <CheckCheck className="h-4 w-4" /> Approve All Eligible
            </button>
            <p className="text-xs text-gray-500 ml-auto" data-testid="admin-referrals-commission-tiers">Commission rate: <strong>50% of customer-paid</strong> · Cooldown <strong>{payoutSummary.cooldownDays} days</strong> · Min payout <strong>€{(payoutSummary.minPayoutEur ?? 20).toFixed(0)}</strong></p>
          </div>

          {/* Payouts list */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {payouts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No payouts in this filter</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payouts-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Helper</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Business</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Plan</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Amount</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Paid On</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {payouts.map(r => {
                      const statusBadge = {
                        pending: 'bg-amber-100 text-amber-800',
                        approved: 'bg-purple-100 text-purple-800',
                        paid: 'bg-green-100 text-green-800',
                        void: 'bg-gray-100 text-gray-600',
                      }[r.payoutStatus] || 'bg-gray-100 text-gray-600';
                      const isRenewal = r.type === 'renewal_commission';
                      return (
                        <tr key={r.id} className="hover:bg-gray-50" data-testid={`payout-row-${r.id}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-900">{r.helperName}</div>
                            <div className="text-xs text-gray-500">{r.helperEmail}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-700">{r.businessName}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded ${isRenewal ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}
                              data-testid={`payout-type-${r.id}`}
                              title={isRenewal ? 'Renewal commission — paid because the referred business renewed their 12-month plan' : 'Initial signup commission'}
                            >
                              {isRenewal ? 'Renewal' : 'Initial'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600 text-xs">{r.planId === 'basic' ? 'Basic' : r.planId === '6months' ? '6-Month' : r.planId === '12months' ? '12-Month' : r.planId || '-'}</td>
                          <td className="px-3 py-2 font-bold text-gray-900">{(r.commissionAmount || 0).toFixed(2)} EUR</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{r.paidAt?.slice(0, 10) || '-'}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge}`}>{r.payoutStatus}</span>
                            {r.eligibleForPayout && r.payoutStatus === 'pending' && (
                              <span className="ml-1 text-[10px] font-medium text-blue-600">eligible</span>
                            )}
                            {!r.eligibleForPayout && r.payoutStatus === 'pending' && !r.refunded && (
                              <span className="ml-1 text-[10px] text-amber-600 inline-flex items-center gap-0.5" title={r.estimatedPayoutDate ? `Eligible from ${r.estimatedPayoutDate}` : ''}><Clock className="h-3 w-3" />cooldown</span>
                            )}
                            {r.refunded && <span className="ml-1 text-[10px] text-red-600">refunded</span>}
                            {r.estimatedPayoutDate && r.payoutStatus === 'pending' && !r.eligibleForPayout && (
                              <div className="text-[10px] text-gray-400 mt-0.5">≈ {new Date(r.estimatedPayoutDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              {r.payoutStatus === 'pending' && r.eligibleForPayout && (
                                <button onClick={() => approvePayout(r.id)} className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700" data-testid={`approve-payout-${r.id}`}>Approve</button>
                              )}
                              {r.payoutStatus === 'approved' && (
                                <>
                                  <button onClick={() => openPayPrompt(r)} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700" data-testid={`mark-paid-${r.id}`}>Mark Paid</button>
                                  <button onClick={() => revokePayout(r.id, false)} className="p-1 text-gray-500 hover:text-amber-600" title="Send back to pending"><RotateCcw className="h-4 w-4" /></button>
                                </>
                              )}
                              {r.payoutStatus !== 'paid' && r.payoutStatus !== 'void' && (
                                <>
                                  <button
                                    onClick={() => { setCommissionModal(r); setCommissionForm({ amount: String(r.commissionAmount || 0), reason: '' }); }}
                                    className="px-2 py-1 text-xs border border-purple-300 text-purple-700 rounded hover:bg-purple-50"
                                    data-testid={`adjust-commission-${r.id}`}
                                    title="Override the commission amount (requires a reason)"
                                  >
                                    Adjust
                                  </button>
                                  <button onClick={() => revokePayout(r.id, true)} className="p-1 text-gray-500 hover:text-red-600" title="Void"><Ban className="h-4 w-4" /></button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payment reference prompt */}
          {payPrompt && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setPayPrompt(null)}>
              <div className="bg-white rounded-xl shadow-xl p-5 max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <h3 className="font-bold mb-1">Mark Payout as Paid</h3>
                <p className="text-xs text-gray-500 mb-3">
                  {payPrompt.helperName} · <span className="font-semibold text-gray-700">{(payPrompt.amount || 0).toFixed(2)} EUR</span>
                </p>

                <div className="mb-4">
                  {renderPayoutDetails(payPrompt.payoutDetails, { compact: true })}
                </div>

                <label className="text-xs text-gray-600 font-medium">Payment Reference (bank transfer ID / PayPal tx / note)</label>
                <input
                  value={payPrompt.ref}
                  onChange={e => setPayPrompt({ ...payPrompt, ref: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono"
                  placeholder="e.g. SEPA-20260419-John"
                  data-testid="payment-reference-input"
                  autoFocus
                />
                <div className="flex gap-2 mt-4 justify-end">
                  <button onClick={() => setPayPrompt(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                  <button onClick={() => markPayoutPaid(payPrompt.id, payPrompt.ref)} className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700" data-testid="confirm-mark-paid">Confirm Paid</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {section === 'helpers' && (
        <>
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <p className="text-sm text-gray-500 font-medium">Total Helpers</p>
          <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="total-helpers">{helpers.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <p className="text-sm text-gray-500 font-medium">Total Referrals</p>
          <p className="text-3xl font-bold text-purple-600 mt-1" data-testid="total-referrals">{referrals.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <p className="text-sm text-gray-500 font-medium">Total Commission Owed</p>
          <p className="text-3xl font-bold text-green-600 mt-1" data-testid="total-commission">{totalCommission.toFixed(2)} EUR</p>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700" data-testid="add-helper-btn">
          <Plus className="h-4 w-4" /> Add Helper
        </button>
        <button onClick={() => setShowAssign(true)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="assign-referral-btn">
          <UserPlus className="h-4 w-4" /> Assign Referral
        </button>
        <button onClick={exportCSV} className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 border" data-testid="export-csv-btn">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Renewal commission announcement broadcast — admin-triggered, idempotent */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="announce-renewal-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <p className="text-sm font-semibold text-emerald-900">Announce the unified 50% commission to existing helpers</p>
            <p className="text-xs text-emerald-800 mt-1">One-off email letting all active helpers know that commissions are now a simple 50% on everything their referred businesses pay — subscriptions, renewals, boosts, and future paid products. Sent in each helper's preferred language (EN / ES) with English fallback. Idempotent — helpers who already received it are skipped automatically.</p>
            {announceState.counts && (
              <p className="text-xs text-emerald-900 mt-2 font-medium" data-testid="announce-renewal-result">
                {announceState.counts.dryRun
                  ? `Preview: would send to ${announceState.counts.total} helper(s).`
                  : `Sent: ${announceState.counts.sent} · Skipped: ${announceState.counts.skipped} · Failed: ${announceState.counts.failed} · Total scanned: ${announceState.counts.total}`}
              </p>
            )}
            {announceState.error && (
              <p className="text-xs text-red-700 mt-2" data-testid="announce-renewal-error">Error: {announceState.error}</p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              disabled={announceState.busy}
              onClick={async () => {
                setAnnounceState({ counts: null, busy: true, error: null });
                try {
                  const res = await axiosInstance.post('/admin/helpers/announce-renewal-commission', { dryRun: true });
                  setAnnounceState({ counts: res.data, busy: false, error: null });
                } catch (e) {
                  setAnnounceState({ counts: null, busy: false, error: e?.response?.data?.detail || e.message });
                }
              }}
              className="px-3 py-1.5 text-sm rounded-lg border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
              data-testid="announce-renewal-preview-btn"
            >
              Preview
            </button>
            <button
              type="button"
              disabled={announceState.busy}
              onClick={async () => {
                if (!window.confirm('Send the 50% commission announcement email to all eligible helpers now? This action cannot be undone (idempotent — helpers who already received it will be skipped).')) return;
                setAnnounceState({ counts: null, busy: true, error: null });
                try {
                  const res = await axiosInstance.post('/admin/helpers/announce-renewal-commission', {});
                  setAnnounceState({ counts: res.data, busy: false, error: null });
                  setMessage({ type: 'success', text: `Announcement sent to ${res.data.sent} helper(s).` });
                } catch (e) {
                  setAnnounceState({ counts: null, busy: false, error: e?.response?.data?.detail || e.message });
                }
              }}
              className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="announce-renewal-send-btn"
            >
              {announceState.busy ? 'Working…' : 'Send announcement'}
            </button>
          </div>
        </div>
      </div>

      {/* Add Helper Modal */}
      {showAdd && (
        <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="add-helper-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">New Helper</h3>
            <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" data-testid="helper-name-input" />
            <input placeholder="Email *" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" data-testid="helper-email-input" />
            <input placeholder="Password *" type="password" value={form.password || ''} onChange={e => setForm({...form, password: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" data-testid="helper-password-input" />
            <input placeholder="Custom code (auto-generated if empty)" value={form.referralCode} onChange={e => setForm({...form, referralCode: e.target.value.toUpperCase()})} className="px-3 py-2 border rounded-lg text-sm font-mono" data-testid="helper-code-input" />
            <input placeholder="Notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <button onClick={createHelper} className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700" data-testid="save-helper-btn">Create Helper</button>
        </div>
      )}

      {/* Assign Referral Modal */}
      {showAssign && (
        <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="assign-referral-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Manually Assign Referral</h3>
            <button onClick={() => setShowAssign(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Business ID" value={assignForm.businessId} onChange={e => setAssignForm({...assignForm, businessId: e.target.value})} className="px-3 py-2 border rounded-lg text-sm font-mono" data-testid="assign-business-id" />
            <select value={assignForm.helperId} onChange={e => setAssignForm({...assignForm, helperId: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" data-testid="assign-helper-select">
              <option value="">Select helper...</option>
              {helpers.map(h => <option key={h.id} value={h.id}>{h.name} ({h.referralCode})</option>)}
            </select>
          </div>
          <button onClick={assignReferral} className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="confirm-assign-btn">Assign</button>
        </div>
      )}

      {/* Helpers table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" /> Helpers
          </h2>
          {/* Iter 177 — sort dropdown. Frontend-only; uses existing data. */}
          <div className="flex items-center gap-2">
            <label htmlFor="helper-sort-by" className="text-xs font-medium text-gray-500">Sort by</label>
            <select
              id="helper-sort-by"
              value={helperSortBy}
              onChange={(e) => setHelperSortBy(e.target.value)}
              className="px-2.5 py-1.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="helpers-sort-by"
            >
              <option value="totalEarned">Total earned (highest first)</option>
              <option value="pendingBalance">Pending commission</option>
              <option value="eligibleBalance">Eligible commission</option>
              <option value="approvedBalance">Approved commission</option>
              <option value="paidBalance">Paid commission</option>
              <option value="founderLeads">Founder leads</option>
              <option value="paidSignups">Paid signups</option>
              <option value="convertedBusinesses">Converted businesses</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : helpers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No helpers yet. Click "Add Helper" to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="helpers-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Affiliate</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Code</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Stats</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(() => {
                  // Iter 177 — derive sortable values once per helper, then
                  // sort a shallow copy. All keys are existing API fields or
                  // helperStats() derivations — no new endpoints.
                  const enriched = helpers.map(h => {
                    const s = helperStats(h.id);
                    return {
                      h,
                      values: {
                        totalEarned: h.totalCommission || 0,
                        pendingBalance: h.pendingBalance || 0,
                        eligibleBalance: h.eligibleBalance || 0,
                        approvedBalance: h.approvedBalance || 0,
                        paidBalance: h.paidBalance || 0,
                        founderLeads: s.founderLeads,
                        paidSignups: s.paidSignups,
                        convertedBusinesses: s.convertedBusinesses,
                      },
                    };
                  });
                  enriched.sort((a, b) => {
                    const av = a.values[helperSortBy] ?? 0;
                    const bv = b.values[helperSortBy] ?? 0;
                    if (bv !== av) return bv - av; // descending
                    // Tie-break by name for a stable visual order
                    return (a.h.name || '').localeCompare(b.h.name || '');
                  });
                  return enriched.map(({ h }) => {
                  const stats = helperStats(h.id);
                  const pending = (h.pendingBalance || 0);
                  const eligible = (h.eligibleBalance || 0);
                  const approved = (h.approvedBalance || 0);
                  const paid = (h.paidBalance || 0);
                  const totalEarned = (h.totalCommission || 0);
                  return (
                  <React.Fragment key={h.id}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedHelper(selectedHelper === h.id ? null : h.id)} data-testid={`helper-row-${h.id}`}>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">{h.name}</div>
                        {h.email && <div className="text-xs text-gray-500">{h.email}</div>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1.5">
                          <code className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-bold">{h.referralCode}</code>
                          <button onClick={(e) => { e.stopPropagation(); copyLink(h.referralCode); }} className="p-1 text-gray-400 hover:text-purple-600" title="Copy referral link">
                            {copiedCode === h.referralCode ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top min-w-[420px]">
                        <div className="grid grid-cols-4 gap-1.5" data-testid={`helper-stats-${h.id}`}>
                          {/* Activity counts */}
                          <StatCell label="Founder leads" value={stats.founderLeads} tone="purple" testId={`stat-founder-leads-${h.id}`} />
                          <StatCell label="Paid signups" value={stats.paidSignups} tone="green" testId={`stat-paid-signups-${h.id}`} />
                          <StatCell label="Converted" value={stats.convertedBusinesses} tone="emerald" testId={`stat-converted-${h.id}`} hint="Founder leads that later upgraded to a paid plan" />
                          <StatCell label="Total earned" value={`${totalEarned.toFixed(2)} EUR`} tone="indigo" testId={`stat-total-earned-${h.id}`} />
                          {/* Commission buckets */}
                          <StatCell label="Pending" value={`${pending.toFixed(2)} EUR`} tone="amber" testId={`stat-pending-${h.id}`} hint="Confirmed commission still in the cooldown window" />
                          <StatCell label="Eligible" value={`${eligible.toFixed(2)} EUR`} tone="sky" testId={`stat-eligible-${h.id}`} hint="Cooldown cleared, waiting for admin approval" />
                          <StatCell label="Approved" value={`${approved.toFixed(2)} EUR`} tone="violet" testId={`stat-approved-${h.id}`} hint="Approved by admin, not yet paid out" />
                          <StatCell label="Paid" value={`${paid.toFixed(2)} EUR`} tone="slate" testId={`stat-paid-${h.id}`} />
                        </div>
                        {h.readyToPay ? (
                          <span className="inline-block mt-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`helper-ready-${h.id}`}>Ready to pay</span>
                        ) : (
                          <span className="inline-block mt-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Below minimum payout</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <button onClick={() => { setEditingId(h.id); setForm({ name: h.name, email: h.email || '', referralCode: h.referralCode, notes: h.notes || '' }); }} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-helper-${h.id}`}>
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => deleteHelper(h.id, h.name)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-helper-${h.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Edit form inline */}
                    {editingId === h.id && (
                      <tr>
                        <td colSpan={4} className="px-4 py-3 bg-blue-50">
                          <div className="flex flex-wrap gap-2 items-center">
                            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="px-2 py-1 border rounded text-sm" placeholder="Name" />
                            <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="px-2 py-1 border rounded text-sm" placeholder="Email" />
                            <input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="px-2 py-1 border rounded text-sm" placeholder="Notes" />
                            <button onClick={() => updateHelper(h.id)} className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium">Save</button>
                            <button onClick={() => setEditingId(null)} className="px-3 py-1 bg-gray-200 text-gray-600 rounded text-xs font-medium">Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Expanded referral details */}
                    {selectedHelper === h.id && (
                      <tr>
                        <td colSpan={4} className="px-4 py-3 bg-gray-50">
                          <div className="mb-3" data-testid={`helper-payout-details-${h.id}`}>
                            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Payout Details</p>
                            {renderPayoutDetails(h.payoutDetails, { compact: true })}
                          </div>
                          {helperReferrals(h.id).length > 0 && (
                            <>
                              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Referral Breakdown</p>
                              <div className="space-y-1">
                                {helperReferrals(h.id).map(r => (
                                  <div key={r.id} className="flex items-center gap-4 text-xs bg-white rounded px-3 py-2 border" data-testid={`referral-detail-${r.id}`}>
                                    <span className="font-medium text-gray-900 min-w-[140px]">{r.businessName}</span>
                                    <span className="text-gray-500">{r.businessEmail}</span>
                                    <span className="text-gray-500">{r.planId || '-'}</span>
                                    <span className={`px-2 py-0.5 rounded-full font-medium ${r.paymentConfirmed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                      {r.paymentConfirmed ? 'Paid' : 'Pending'}
                                    </span>
                                    <span className="font-bold text-green-700">{r.commissionAmount > 0 ? `${r.commissionAmount.toFixed(2)} EUR` : '-'}</span>
                                    <span className="text-gray-400">{r.createdAt?.slice(0, 10)}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );});})()}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}

      {/* Commission override modal — Feb 2026 admin hardening */}
      {commissionModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="commission-modal">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Adjust Commission</h3>
                <button onClick={() => { setCommissionModal(null); setCommissionForm({ amount: '', reason: '' }); }} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-sm text-gray-600 mb-1">Helper: <strong>{commissionModal.helperName}</strong></p>
              <p className="text-sm text-gray-600 mb-4">Business: <strong>{commissionModal.businessName}</strong> · Plan: <strong>{commissionModal.planId || '-'}</strong></p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">New amount (EUR)</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={commissionForm.amount}
                    onChange={(e) => setCommissionForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                    data-testid="commission-amount-input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Reason (required)</label>
                  <textarea
                    rows={3} maxLength={500}
                    value={commissionForm.reason}
                    onChange={(e) => setCommissionForm(f => ({ ...f, reason: e.target.value }))}
                    placeholder="e.g. Bonus for high-performing helper; correction after payout error; ..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                    data-testid="commission-reason-input"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">{commissionForm.reason.length}/500</p>
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => { setCommissionModal(null); setCommissionForm({ amount: '', reason: '' }); }} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
                <button
                  onClick={submitCommissionOverride}
                  disabled={!commissionForm.reason.trim() || commissionForm.amount === ''}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
                  data-testid="commission-save-btn"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReferrals;

// Iter 176 — compact stat tile used inside the helpers table. Renders a
// small labelled value chip with a tone-specific colour so the eye can
// scan the per-helper breakdown without horizontal scrolling.
const TONE_MAP = {
  purple: 'bg-purple-50 text-purple-800 border-purple-200',
  green: 'bg-green-50 text-green-800 border-green-200',
  emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  sky: 'bg-sky-50 text-sky-800 border-sky-200',
  violet: 'bg-violet-50 text-violet-800 border-violet-200',
  slate: 'bg-slate-50 text-slate-800 border-slate-200',
};
const StatCell = ({ label, value, tone = 'slate', hint, testId }) => (
  <div
    className={`rounded-md border px-2 py-1.5 ${TONE_MAP[tone] || TONE_MAP.slate}`}
    title={hint || label}
    data-testid={testId}
  >
    <div className="text-[10px] uppercase tracking-wide opacity-75 leading-none">{label}</div>
    <div className="text-sm font-bold tabular-nums mt-1 leading-tight">{value}</div>
  </div>
);
